'use client';

/**
 * ProjectDetailsTab
 *
 * Details tab content for project page.
 * Shows editable form for name, slug, description, repo URL, and default branch.
 */

import {
  Alert,
  AlertDescription,
  Button,
  Inline,
  Input,
  Label,
  Stack,
  Text,
  TextArea,
} from '@cortex/ui';
import React, { useState } from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

interface Project {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  repo_url: string | null;
  default_branch: string;
}

interface ProjectDetailsTabProps {
  project: Project;
  onUpdate: () => void;
}

export function ProjectDetailsTab({
  project,
  onUpdate,
}: ProjectDetailsTabProps) {
  const fetchWithAuth = useFetchWithAuth();
  const [name, setName] = useState(project.name);
  const [slug, setSlug] = useState(project.slug);
  const [description, setDescription] = useState(project.description || '');
  const [repoUrl, setRepoUrl] = useState(project.repo_url || '');
  const [defaultBranch, setDefaultBranch] = useState(project.default_branch);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetchWithAuth(`/admin/projects/${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          slug,
          description: description || null,
          repositoryUrl: repoUrl || undefined,
          defaultBranch,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update project');
      }

      setSuccess('Project updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    name !== project.name ||
    slug !== project.slug ||
    description !== (project.description || '') ||
    repoUrl !== (project.repo_url || '') ||
    defaultBranch !== project.default_branch;

  return (
    <Stack gap="lg">
      {/* Error/Success Messages */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Project Name */}
      <Stack gap="xs">
        <Label htmlFor="projectName">Project Name</Label>
        <Input
          id="projectName"
          type="text"
          placeholder="My Project"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1"
        />
      </Stack>

      {/* Slug */}
      <Stack gap="xs">
        <Label htmlFor="projectSlug">Slug</Label>
        <Input
          id="projectSlug"
          type="text"
          placeholder="my-project"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="mt-1"
        />
        <Text color="muted" size="xs" className="mt-1">
          URL-friendly identifier. Only lowercase letters, numbers, and hyphens.
        </Text>
      </Stack>

      {/* Description */}
      <Stack gap="xs">
        <Label htmlFor="projectDescription">Description</Label>
        <TextArea
          id="projectDescription"
          placeholder="A brief description of the project..."
          value={description}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setDescription(e.target.value)
          }
          className="mt-1"
          rows={3}
        />
        <Text color="muted" size="xs" className="mt-1">
          Optional description for display in the UI.
        </Text>
      </Stack>

      {/* Repository URL */}
      <Stack gap="xs">
        <Label htmlFor="repoUrl">Repository URL</Label>
        <Input
          id="repoUrl"
          type="url"
          placeholder="https://github.com/org/repo"
          value={repoUrl}
          onChange={(e) => setRepoUrl(e.target.value)}
          className="mt-1"
        />
        <Text color="muted" size="xs" className="mt-1">
          Full URL to the Git repository.
        </Text>
      </Stack>

      {/* Default Branch */}
      <Stack gap="xs">
        <Label htmlFor="defaultBranch">Default Branch</Label>
        <Input
          id="defaultBranch"
          type="text"
          placeholder="main"
          value={defaultBranch}
          onChange={(e) => setDefaultBranch(e.target.value)}
          className="mt-1"
        />
        <Text color="muted" size="xs" className="mt-1">
          The default branch for git operations (e.g., main, master).
        </Text>
      </Stack>

      {/* Save Button */}
      <Inline justify="end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          loading={saving}
        >
          Save Changes
        </Button>
      </Inline>
    </Stack>
  );
}
